import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { removeFromCart, clearCart } from "../../../Store/slices/cartSlice";
import "../../../shared/styles/CartShop.css";
import { Form, message, Select, Input } from "antd";
import { useTranslation } from "react-i18next";
import { formatCurrency, toApiNumber } from "../../../utils/formatters";
import { apiClient } from "../../../shared/api/AllApi";
import { fetchSystemConfig, readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { fetchExchangeRates, EXCHANGE_RATES_UPDATED_EVENT } from "../../../shared/lib/exchangeRates";
import {
  useAccountantsCatalog,
  useCustomersCatalog,
} from "../../../hooks/useCatalogData";

const CartShop = ({ cart }) => {
  const { t, i18n } = useTranslation();
  const mainContentRef = useRef(null);
  const [subAmount, setSubAmount] = useState(0);
  const [customer, setCustomer] = useState(null);
  const [accountant, setAccountant] = useState(null);
  const [amountPaid, setAmountPaid] = useState(0);
  const [amountPaidMain, setAmountPaidMain] = useState(0);
  const [amountPaidSecondary, setAmountPaidSecondary] = useState(0);
  const [exchangeRateId, setExchangeRateId] = useState(null);
  const [systemConfig, setSystemConfig] = useState(() => readCachedSystemConfig());
  const [exchangeRates, setExchangeRates] = useState([]);
  const {
    data: customerOptions,
    loading: customersLoading,
  } = useCustomersCatalog();
  const { data: accountants, loading: accountantsLoading } =
    useAccountantsCatalog();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const isMobile = window.innerWidth <= 768;
  const [localSubQuantities, setLocalSubQuantities] = useState({});
  const [manuallyChangedSubQuantities, setManuallyChangedSubQuantities] = useState({});
  const [editedSubtotals, setEditedSubtotals] = useState({});
  const [editedPrices, setEditedPrices] = useState({});
  const [editedSubUnitPrices, setEditedSubUnitPrices] = useState({});

  const getTranslatedName = useCallback(
    (nameInput) => {
      let nameTranslations;
      try {
        nameTranslations =
          typeof nameInput === "string" ? JSON.parse(nameInput) : nameInput;
      } catch (error) {
        return "Unnamed Product";
      }
      return nameTranslations?.[i18n.language] || nameTranslations?.en || "N/A";
    },
    [i18n.language]
  );

  const calculateSubAmount = useCallback(() => {
    let total = 0;
    cart.forEach((item) => {
      // Use edited subtotal if available, otherwise calculate from price and quantity
      const subtotal = editedSubtotals[item.id] !== undefined
        ? editedSubtotals[item.id]
        : item.quantity * parseFloat(item.price || 0);
      total += subtotal;
    });
    setSubAmount(total);
  }, [cart, editedSubtotals]);
  const handleCustomerChange = useCallback((value) => {
    setCustomer(value);
  }, []);

  const handleAccountantChange = useCallback((value) => {
    setAccountant(value);
  }, []);

  const handleAmountPaidChange = useCallback((e) => {
    setAmountPaid(Number(e.target.value));
  }, []);

  const useDualCurrency = Boolean(systemConfig?.secondary_currency_code);

  const selectedRate = useMemo(() => {
    if (!exchangeRateId || !exchangeRates.length) return null;
    return exchangeRates.find((r) => Number(r.id) === Number(exchangeRateId)) || null;
  }, [exchangeRateId, exchangeRates]);

  const appliedMainTotal = useMemo(() => {
    const main = toApiNumber(amountPaidMain, 0);
    const sec = toApiNumber(amountPaidSecondary, 0);
    if (!useDualCurrency) {
      return toApiNumber(amountPaid, 0);
    }
    const secondaryPerMain = selectedRate ? toApiNumber(selectedRate.main_per_secondary, 0) : 0;
    if (!secondaryPerMain) return Math.round(main * 100) / 100;
    return Math.round((main + sec / secondaryPerMain) * 100) / 100;
  }, [amountPaid, amountPaidMain, amountPaidSecondary, selectedRate, useDualCurrency]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await fetchSystemConfig();
        if (cancelled) return;
        setSystemConfig(cfg);
        if (!cfg?.secondary_currency_code) {
          setExchangeRates([]);
          setExchangeRateId(null);
          return;
        }
        const rates = await fetchExchangeRates();
        if (cancelled) return;
        setExchangeRates(rates);
        setExchangeRateId((prev) =>
          prev && rates.some((r) => Number(r.id) === Number(prev)) ? prev : rates[0]?.id ?? null
        );
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onFxUpdated = () => {
      (async () => {
        try {
          const cfg = await fetchSystemConfig();
          setSystemConfig(cfg);
          if (!cfg?.secondary_currency_code) {
            setExchangeRates([]);
            setExchangeRateId(null);
            return;
          }
          const rates = await fetchExchangeRates();
          setExchangeRates(rates);
          setExchangeRateId((prev) =>
            prev && rates.some((r) => Number(r.id) === Number(prev)) ? prev : rates[0]?.id ?? null
          );
        } catch {
          /* ignore */
        }
      })();
    };
    window.addEventListener(EXCHANGE_RATES_UPDATED_EVENT, onFxUpdated);
    return () => window.removeEventListener(EXCHANGE_RATES_UPDATED_EVENT, onFxUpdated);
  }, []);

  const handleRemove = useCallback(
    (id) => {
      dispatch(removeFromCart(id));
    },
    [dispatch]
  ); const handleUpdateQuantity = useCallback(
    (itemId, change) => {
      const item = cart.find(i => i.id === itemId);
      if (!item) return;

      const newQuantity = item.quantity + change;

      if (newQuantity < 0) {
        message.warning(t("Quantity cannot be less than 0."));
        return;
      }

      if (item.stock !== undefined && newQuantity > item.stock) {
        message.warning(t("Cannot add more than {{stock}} items. This is all we have in stock.", { stock: item.stock }));
        // Set to maximum available if trying to add more
        const adjustedChange = item.stock - item.quantity;
        if (adjustedChange > 0) {
          dispatch({ type: 'cart/updateQuantity', payload: { itemId, change: adjustedChange } });
        }
        return;
      }

      dispatch({ type: 'cart/updateQuantity', payload: { itemId, change } });

      if (!manuallyChangedSubQuantities[itemId]) {
        setLocalSubQuantities(prev => ({
          ...prev,
          [itemId]: newQuantity * (item.sub_unit_ratio || 1)
        }));
      }

      // Update subtotal when quantity changes
      if (editedPrices[itemId] !== undefined) {
        const price = editedPrices[itemId];
        const subtotal = price * newQuantity;
        const subUnitRatio = item.sub_unit_ratio || 1;
        const subQty = newQuantity * subUnitRatio;

        setEditedSubtotals(prev => ({ ...prev, [itemId]: subtotal }));

        // Update sub unit price if sub quantity exists
        if (subQty > 0) {
          setEditedSubUnitPrices(prev => ({ ...prev, [itemId]: subtotal / subQty }));
        }
      }
    },
    [dispatch, cart, manuallyChangedSubQuantities, editedPrices, setEditedSubtotals, setEditedSubUnitPrices, t]
  );

  const handleLocalSubQuantityChange = useCallback((itemId, value) => {
    const numericValue = parseFloat(value) || 0;
    if (numericValue >= 0) {
      setLocalSubQuantities(prev => ({ ...prev, [itemId]: numericValue }));
      // Mark this sub_qty as manually changed
      setManuallyChangedSubQuantities(prev => ({ ...prev, [itemId]: true }));
    }
  }, []);

  const submitOrder = useCallback(async () => {
    if (!customer) {
      message.error(t("Please select a customer"));
      return;
    }
    if (!accountant) {
      message.error(t("Please select an accountant"));
      return;
    }    // Find the selected accountant's full information
    const selectedAccountant = accountants.find(acc => acc.id === accountant || acc.id === parseInt(accountant, 10));

    // Get the accountant name to send to backend
    const accountantName = selectedAccountant ? selectedAccountant.name : '';

    // Save order to localStorage as a backup
    const orderBackup = {
      customer_id: customer,
      accountant: accountantName,
      amount_paid: amountPaid,
      amount_paid_main: amountPaidMain,
      amount_paid_secondary: amountPaidSecondary,
      exchange_rate_id: exchangeRateId,
      cart: cart,
      localSubQuantities: localSubQuantities,
    };
    localStorage.setItem("orderBackup", JSON.stringify(orderBackup));

    const toMoney = (value) => Math.round(toApiNumber(value, 0) * 100) / 100;
    const dual = Boolean(systemConfig?.secondary_currency_code);
    const secVal = toMoney(amountPaidSecondary);
    if (dual && secVal > 0 && !exchangeRateId) {
      message.error(t("Please select an exchange rate."));
      return;
    }

    const orderJson = {
      customer_id: customer,
      accountant: accountantName,
      order_items: cart.map((item) => {
        const subQty =
          localSubQuantities[item.id] !== undefined
            ? localSubQuantities[item.id]
            : item.quantity * (item.sub_unit_ratio || 1);

        const singlePrice = toMoney(
          editedPrices[item.id] !== undefined
            ? editedPrices[item.id]
            : item.price
        );

        const subtotal =
          editedSubtotals[item.id] !== undefined
            ? toMoney(editedSubtotals[item.id])
            : toMoney(item.quantity * singlePrice);

        // Calculate sub_price from single_price and sub_unit_ratio
        const subUnitRatio = item.sub_unit_ratio || 1;
        let subUnitPrice =
          editedSubUnitPrices[item.id] !== undefined
            ? editedSubUnitPrices[item.id]
            : subUnitRatio > 0
              ? singlePrice / subUnitRatio
              : (item.sub_unit_price || 0);

        // Round values
        const roundedSinglePrice = toMoney(singlePrice);
        const roundedSubQty = toMoney(subQty);
        const roundedSubtotal = toMoney(subtotal);

        // Calculate sub_price so that sub_price × sub_qty equals subtotal exactly
        // This prevents backend from recalculating incorrectly
        let roundedSubPrice;
        if (roundedSubQty > 0) {
          roundedSubPrice = toMoney(roundedSubtotal / roundedSubQty);
        } else {
          roundedSubPrice = toMoney(subUnitPrice);
        }

        return {
          product_id: item.id,
          quantity: item.quantity,
          single_price: roundedSinglePrice,
          sub_qty: roundedSubQty,
          sub_price: roundedSubPrice,
          subtotal: roundedSubtotal,
        };
      }),
    };

    if (dual) {
      orderJson.amount_paid_main = toMoney(amountPaidMain);
      orderJson.amount_paid_secondary = secVal;
      if (secVal > 0) {
        orderJson.exchange_rate_id = exchangeRateId;
      }
    } else {
      orderJson.amount_paid = toMoney(amountPaid);
    }

    try {
      const response = await apiClient.post("/orders", orderJson);
      const result = response.data; if (result.id) {        // Clear the cart and localStorage after successful order
        dispatch(clearCart());
        localStorage.removeItem('orderBackup'); // Remove the backup order
        message.success(t("Your order has been submitted successfully"));
        window.location.href = `/TableOrder/InfoOrder/${result.id}`;
      } else {
        message.error(t("Failed to submit your order"));
      }
    } catch (error) {
      console.error("Error submitting order:", error);
      const msg = error?.response?.data?.message;
      message.error(msg || t("Failed to submit your order"));
    }
  }, [
    cart,
    customer,
    accountant,
    accountants,
    amountPaid,
    amountPaidMain,
    amountPaidSecondary,
    exchangeRateId,
    systemConfig,
    t,
    localSubQuantities,
    editedSubtotals,
    editedSubUnitPrices,
    editedPrices,
    dispatch,
  ]);

  useEffect(() => {
    calculateSubAmount();

    // Initialize sub quantities for new items only
    setLocalSubQuantities(prev => {
      const updated = { ...prev };
      let hasChanges = false;

      cart.forEach(item => {
        if (!prev.hasOwnProperty(item.id)) {
          updated[item.id] = item.quantity * (item.sub_unit_ratio || 1);
          hasChanges = true;
        }
      });

      // Remove sub quantities for items no longer in cart
      Object.keys(prev).forEach(key => {
        if (!cart.find(item => item.id === parseInt(key))) {
          delete updated[key];
          hasChanges = true;
        }
      });

      return hasChanges ? updated : prev;
    });

    // Reset manual changes when cart changes
    setManuallyChangedSubQuantities({});
  }, [cart, calculateSubAmount]);

  // Recalculate total when cart or edited values change
  useEffect(() => {
    calculateSubAmount();
  }, [calculateSubAmount]);

  // Separate effect for restoring pending order - only run once accountants are loaded
  useEffect(() => {
    // Only try to restore if we have accountants data
    if (accountants.length === 0) return;

    // Check if there's a pending order in localStorage
    try {
      const pendingOrder = localStorage.getItem('orderBackup');
      if (pendingOrder && cart.length === 0) {
        const orderData = JSON.parse(pendingOrder); if (orderData.customer_id) {
          setCustomer(orderData.customer_id);
          form.setFieldsValue({ customer: orderData.customer_id });
        }
        // Find accountant by name if available in the backup
        if (orderData.accountant && accountants.length > 0) {
          const matchedAccountant = accountants.find(acc => acc.name === orderData.accountant);
          if (matchedAccountant) {
            setAccountant(matchedAccountant.id);
            form.setFieldsValue({ accountant: matchedAccountant.id });
          }
        }
        if (orderData.amount_paid_main !== undefined || orderData.amount_paid_secondary !== undefined) {
          setAmountPaidMain(Number(orderData.amount_paid_main) || 0);
          setAmountPaidSecondary(Number(orderData.amount_paid_secondary) || 0);
          if (orderData.exchange_rate_id) setExchangeRateId(orderData.exchange_rate_id);
        } else if (orderData.amount_paid) {
          setAmountPaid(orderData.amount_paid);
          setAmountPaidMain(Number(orderData.amount_paid) || 0);
          form.setFieldsValue({ amount_paid: orderData.amount_paid });
        }
        if (orderData.localSubQuantities) {
          setLocalSubQuantities(orderData.localSubQuantities);
        }
        // Note: cart is handled by Redux and loaded separately via localStorage
      }
    } catch (error) {
      console.error("Error restoring pending order:", error);
    }
  }, [accountants, cart.length, form]);
  // Returns the subtotal for an item (edited or default)
  const getItemSubtotal = (item) => {
    return editedSubtotals[item.id] !== undefined
      ? editedSubtotals[item.id]
      : item.quantity * parseFloat(item.price || 0);
  };

  // Handles changes to the price input
  const handlePriceChange = (itemId, newPrice) => {
    const price = parseFloat(newPrice) || 0;
    const item = cart.find(i => i.id === itemId);
    const qty = item.quantity;
    const subUnitRatio = item.sub_unit_ratio || 1;

    // Calculate sub price from price and sub unit ratio (price / sub_unit_ratio = sub_price)
    const subPrice = subUnitRatio > 0 ? price / subUnitRatio : 0;

    // Calculate subtotal from price and quantity
    const subtotal = price * qty;

    setEditedPrices(prev => ({ ...prev, [itemId]: price }));
    setEditedSubtotals(prev => ({ ...prev, [itemId]: subtotal }));
    setEditedSubUnitPrices(prev => ({ ...prev, [itemId]: subPrice }));
  };  // Table columns definition

  const cartTable = (
    <div style={{ overflowX: "auto", width: "100%" }}>
      <Table style={{ minWidth: isMobile ? "100%" : "600px" }}>
        <TableHead>
          <TableRow>
            {[
              "Product",
              "Quantity",
              "یەکەی ژێر",
              "پێوانەی یەکە",
              "تەواوی ژمارەی یەکەی ژێرین",
              "نرخی یەک ژێر یەکە",
              "نرخ",
              "Sub Total",
              "Actions",
            ].map((key) => (
              <TableCell
                key={key}
                style={{
                  fontSize: isMobile ? "12px" : "16px",
                  fontWeight: "bold",
                }}
              >
                {t(key)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {cart.map((item) => (
            <TableRow key={item.id}>
              <TableCell style={{ fontSize: isMobile ? "12px" : "14px", fontWeight: "bold" }}>
                {getTranslatedName(item.name)}
              </TableCell>              <TableCell style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Button
                  onClick={() => handleUpdateQuantity(item.id, 1)}
                  style={{ fontWeight: "bold", minWidth: "30px" }}
                >
                  +
                </Button>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Input
                    type="number"
                    value={Math.floor(item.quantity)}
                    style={{
                      width: '80px',
                      margin: '0 5px',
                      textAlign: 'center',
                      borderColor: item.stock !== undefined && item.quantity >= item.stock ? '#ff4d4f' : undefined
                    }}
                    min={0}
                    max={item.stock}
                    step={1}
                    onKeyDown={(e) => {
                      // Prevent decimal point
                      if (e.key === '.') {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      const newValue = parseInt(e.target.value, 10);
                      if (!isNaN(newValue)) {
                        const change = newValue - item.quantity;
                        handleUpdateQuantity(item.id, change);
                      }
                    }}
                    onBlur={(e) => {
                      const value = parseInt(e.target.value, 10) || 0;
                      if (value < 0) {
                        handleUpdateQuantity(item.id, -item.quantity);
                      } else if (item.stock !== undefined && value > item.stock) {
                        handleUpdateQuantity(item.id, item.stock - item.quantity);
                      }
                    }}
                  />
                  {item.stock !== undefined && (
                    <span style={{
                      fontSize: '11px',
                      color: item.quantity >= item.stock ? '#ff4d4f' : '#52c41a',
                      marginTop: '2px'
                    }}>
                      {item.quantity >= item.stock
                        ? t("Out of stock!")
                        : t("{left} left", { left: item.stock - item.quantity })}
                    </span>
                  )}
                </div>
                <Button
                  onClick={() => handleUpdateQuantity(item.id, -1)}
                  style={{ fontWeight: "bold", minWidth: "30px" }}
                >
                  -
                </Button>
              </TableCell>
              <TableCell>{(localSubQuantities[item.id] === 0) ? "-" : getTranslatedName(item.sub_unit?.name)}</TableCell>
              <TableCell>{(localSubQuantities[item.id] === 0) ? "-" : (item.sub_unit_ratio || "")}</TableCell>
              <TableCell>
                <Input
                  type="number"
                  min="0"
                  step="any"
                  value={localSubQuantities[item.id] !== undefined ? localSubQuantities[item.id] : item.quantity * (item.sub_unit_ratio || 1)}
                  onChange={(e) => handleLocalSubQuantityChange(item.id, e.target.value)}
                  onBlur={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    handleLocalSubQuantityChange(item.id, value);
                  }}
                  style={{ width: "80px" }}
                />
              </TableCell>
              <TableCell>
                {(
                  editedSubUnitPrices[item.id] !== undefined
                    ? editedSubUnitPrices[item.id]
                    : (getItemSubtotal(item) / (localSubQuantities[item.id] || 1))
                ).toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </TableCell>
              <TableCell>
                <Input
                  type="number"
                  min="0"
                  step="any"
                  value={
                    editedPrices[item.id] !== undefined
                      ? editedPrices[item.id]
                      : parseFloat(item.price || 0)
                  }
                  onChange={(e) => handlePriceChange(item.id, e.target.value)}
                  style={{ width: "100px" }}
                />
              </TableCell>
              <TableCell>
                {getItemSubtotal(item).toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </TableCell>
              <TableCell>
                <Button
                  onClick={() => handleRemove(item.id)}
                  style={{
                    background: "#FA4032",
                    color: "white",
                    fontSize: "12px",
                  }}
                >
                  {t("Remove")}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  const checkoutForm = (
    <div style={{ marginTop: "30px" }}>
      <Form
        form={form}
        onFinish={submitOrder}
        className="cart-form"
        layout="vertical"
        style={{
          maxWidth: "400px",
          margin: "0 auto",
        }}
      >        <Form.Item
        label={t("Select Customer")}
        name="customer"
        rules={[{ required: true, message: t("Please select customer") }]}
      >
          <Select
            placeholder={t("Select a customer")}
            onChange={handleCustomerChange}
            loading={customersLoading}
          >
            {customerOptions.map((cust) => (
              <Select.Option key={cust.id} value={cust.id}>
                {cust.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label={t("Select Accountant")}
          name="accountant"
          rules={[{ required: true, message: t("Please select accountant") }]}
        >
          <Select
            placeholder={t("Select an accountant")}
            onChange={handleAccountantChange}
            loading={accountantsLoading}
          >
            {accountants.map((acc) => (
              <Select.Option key={acc.id} value={acc.id}>
                {acc.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {useDualCurrency ? (
          <>
            <Form.Item label={`${t("Amount Paid")} (${systemConfig?.currency_code || ""})`}>
              <Input
                type="number"
                min={0}
                value={amountPaidMain}
                onChange={(e) => setAmountPaidMain(Number(e.target.value))}
              />
            </Form.Item>
            <Form.Item label={`${t("Amount Paid")} (${systemConfig?.secondary_currency_code || ""})`}>
              <Input
                type="number"
                min={0}
                value={amountPaidSecondary}
                onChange={(e) => setAmountPaidSecondary(Number(e.target.value))}
              />
            </Form.Item>
            {exchangeRates.length > 0 && (
              <Form.Item label={t("Exchange rate")}>
                <Select
                  placeholder={t("Select rate")}
                  value={exchangeRateId}
                  onChange={setExchangeRateId}
                  style={{ width: "100%" }}
                >
                  {exchangeRates.map((r) => (
                    <Select.Option key={r.id} value={r.id}>
                      {String(r.effective_from || "").slice(0, 10)} — 1 {systemConfig?.currency_code} ={" "}
                      {r.main_per_secondary} {systemConfig?.secondary_currency_code}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            <Typography variant="body2" style={{ marginBottom: 12 }}>
              {t("Total applied")} ({systemConfig?.currency_code}):{" "}
              <strong>{formatCurrency(appliedMainTotal, systemConfig?.currency_code)}</strong>
            </Typography>
          </>
        ) : (
          <Form.Item
            label={t("Amount Paid")}
            name="amount_paid"
            rules={[{ required: true, message: t("Please enter paid amount") }]}
          >
            <Input type="number" value={amountPaid} onChange={handleAmountPaidChange} />
          </Form.Item>
        )}

        <Form.Item>
          <Button
            type="submit"
            style={{
              background: "#52c41a",
              color: "white",
              fontSize: "16px",
              padding: "6px 20px",
            }}
          >
            {t("Submit Order")}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );

  if (cart.length > 0) {
    return (
      <div className="cart-page" style={{ direction: "rtl", padding: "24px" }}>
        <article>
          <div ref={mainContentRef} tabIndex="-1" aria-labelledby="cart-content">
            {cartTable}
            <div className="total" style={{ marginTop: "20px" }}>
              <Typography variant="h6">{t("Cart Subtotal")}:</Typography>
              <Typography variant="h6" color="green">
                {formatCurrency(subAmount, systemConfig?.currency_code)}
              </Typography>
            </div>
            {checkoutForm}
          </div>
        </article>
      </div>
    );
  } else {
    return (
      <div className="cart-page" style={{ textAlign: "center", marginTop: "60px", padding: "24px" }}>
        <Typography variant="h5" style={{ fontWeight: 700, color: "#64748b" }}>
          {t("Your cart is empty.")}
        </Typography>
      </div>
    );
  }
};

export default CartShop;
